import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { MainSearchModal } from './MainSearchModal'
import { DATA_SEARCH_INIT, stayDayOptions, useModalScrollLock } from './utils'
import { IconCalendar, IconHuman } from './Icon'
import { useTopPageStore } from '../../store/topPageStore'

export const MainSearchBar: React.FC = () => {
  const { t } = useTranslation()
  const { isSearchModalOpen, dataSearch, initializeFromUrl, openSearchModal } = useTopPageStore()
  const STAY_DAY_OPTIONS = useMemo(() => stayDayOptions(t), [t])

  useEffect(() => {
    initializeFromUrl()
  }, [initializeFromUrl])

  useModalScrollLock(isSearchModalOpen)

  const renderMainSearchBarContent = () => {
    const isDataSearchEmpty = () => {
      const hasSelectedBirthday = dataSearch.birthdays?.some((birthday) => birthday.year && birthday.month && birthday.day)
      return (
        dataSearch.maximum_number_of_adults === DATA_SEARCH_INIT.maximum_number_of_adults &&
        dataSearch.number_of_preschoolers_1 === DATA_SEARCH_INIT.number_of_preschoolers_1 &&
        dataSearch.number_of_students_1 === DATA_SEARCH_INIT.number_of_students_1 &&
        dataSearch.number_of_study_abroad === DATA_SEARCH_INIT.number_of_study_abroad &&
        dataSearch.accept_class.length === 0 &&
        dataSearch.plan_month.length === 0 &&
        dataSearch.stay_day.length === 0 &&
        !dataSearch.start_date &&
        !dataSearch.end_date &&
        !hasSelectedBirthday
      )
    }

    const hasAdultsOrChildren =
      dataSearch.maximum_number_of_adults > 0 ||
      dataSearch.number_of_preschoolers_1 > 0 ||
      dataSearch.number_of_students_1 > 0 ||
      dataSearch.number_of_study_abroad > 0

    const hasPlanMonth = dataSearch.plan_month?.filter((item) => item)?.length > 0
    const hasStayDay = dataSearch.stay_day?.length > 0
    const hasStartDate = Boolean(dataSearch.start_date)

    // 期間と人数の両方がある場合は縦並び
    const hasDateInfo = hasPlanMonth || hasStayDay || hasStartDate
    const shouldStackVertically = hasDateInfo && hasAdultsOrChildren

    // SearchCalendar風の日付 ~ 滞在日数表示
    const renderDateAndStayInfo = () => {
      // 開始日がある場合は開始日を優先表示、ない場合は月を表示
      const startDateText = hasStartDate ? dataSearch.start_date.replaceAll('-', '/') : ''
      const monthText = hasPlanMonth ? dataSearch.plan_month.sort()[0]?.replace('/', '/') : ''
      const dateText = startDateText || monthText

      // 滞在日数の表示（複数ある場合は最初のもの）
      // stay_dayがURLにない場合は、start_date/end_dateの差分（日数+1）から補完する
      const fallbackStayDayValue = (() => {
        if (hasStayDay || !dataSearch.start_date || !dataSearch.end_date) return ''
        const start = new Date(`${dataSearch.start_date}T00:00:00`)
        const end = new Date(`${dataSearch.end_date}T00:00:00`)
        if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return ''
        const diffDays = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1
        return diffDays > 0 ? String(diffDays) : ''
      })()
      const stayDayValue = hasStayDay ? dataSearch.stay_day[0] : fallbackStayDayValue
      const stayDayText = STAY_DAY_OPTIONS.find((option) => option.value === stayDayValue)?.name || ''

      if (dateText || stayDayText) {
        return (
          <div className="flex items-center justify-start gap-1">
            <div className="shrink-0">
              <IconCalendar />
            </div>
            <div className="font-size-14 overflow-hidden text-ellipsis whitespace-nowrap">
              {dateText} {dateText && stayDayText ? '〜' : ''} {stayDayText}
            </div>
            {hasAdultsOrChildren && (
              <>
                {/* モバイル: 常に区切り線を表示 */}
                <div className="shrink-0 md:hidden">|</div>
                {/* PC: 横並びの時のみ区切り線を表示 */}
                {!shouldStackVertically && <div className="shrink-0 hidden md:block">|</div>}
              </>
            )}
          </div>
        )
      }
      return null
    }

    // SearchCalendar風の人数表示
    const renderPeopleInfo = () => {
      if (!hasAdultsOrChildren) return null

      const totalChildren =
        (dataSearch.number_of_preschoolers_1 || 0) +
        (dataSearch.number_of_students_1 || 0) +
        (dataSearch.number_of_study_abroad || 0)

      return (
        <div className="flex items-center gap-1">
          <div className="shrink-0">
            <IconHuman />
          </div>
          <div className="font-size-14 overflow-hidden text-ellipsis whitespace-nowrap">
            {t('plan_detail.reservation.modal.people.adult')}
            {dataSearch.maximum_number_of_adults}
            {t('reservation_confirm.modal_booking.people')} {t('plan_detail.reservation.modal.people.child')}
            {totalChildren}
            {t('reservation_confirm.modal_booking.people')}
          </div>
        </div>
      )
    }

    return (
      <div className="w-full overflow-hidden">
        {isDataSearchEmpty() && (
          <div className="font-size-14 overflow-hidden whitespace-nowrap text-left text-ellipsis">
            {t('top.modal_search.default_placeholder')}
          </div>
        )}

        {!isDataSearchEmpty() && (
          <>
            {/* モバイル表示: 常に横並び */}
            <div className="flex md:hidden items-center justify-start gap-2">
              {renderDateAndStayInfo()}
              {renderPeopleInfo()}
            </div>
            {/* PC表示: 条件によって縦/横並び */}
            <div
              className={`hidden md:${
                shouldStackVertically ? 'flex flex-col items-start gap-0' : 'flex items-center justify-start gap-2'
              }`}
            >
              {renderDateAndStayInfo()}
              {shouldStackVertically && <div className="w-full border-t border-gray-300 my-1 mt-2 mb-2"></div>}
              {renderPeopleInfo()}
            </div>
          </>
        )}
      </div>
    )
  }

  return (
    <>
      <div className={`w-full flex items-center justify-center block`}>
        <button onClick={openSearchModal} className="all-unset border flex items-center button-main-search-bar">
          {renderMainSearchBarContent()}
        </button>
      </div>
      {isSearchModalOpen && <MainSearchModal />}
    </>
  )
}
